// src/pages/Notifications.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

function Notifications() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [userId, setUserId] = useState('');
  const [userType, setUserType] = useState('all');
  const [targetType, setTargetType] = useState('all');
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  
  // Search state for notification history
  const [searchTerm, setSearchTerm] = useState('');
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const API_URL = 'https://boda-backend-wuea.onrender.com';

  // Load notification history on mount
  useEffect(() => {
    fetchNotificationHistory();
  }, []);

  // Filter history when search term changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredHistory(notificationHistory);
      return;
    }
    const term = searchTerm.toLowerCase().trim();
    const filtered = notificationHistory.filter(n => {
      const title = (n.title || '').toLowerCase();
      const body = (n.body || '').toLowerCase();
      const type = (n.type || '').toLowerCase();
      const userType = (n.user_type || '').toLowerCase();
      const recipient = (n.recipient || '').toLowerCase();
      
      return title.includes(term) || 
             body.includes(term) || 
             type.includes(term) || 
             userType.includes(term) ||
             recipient.includes(term);
    });
    setFilteredHistory(filtered);
  }, [searchTerm, notificationHistory]);

const fetchNotificationHistory = async () => {
    try {
        setLoadingHistory(true);
        const token = localStorage.getItem('token');
        
        // Try to fetch from backend, but don't fail if endpoint doesn't exist
        try {
            const response = await axios.get(`${API_URL}/api/admin/notification-history`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            setNotificationHistory(response.data.notifications || []);
            setFilteredHistory(response.data.notifications || []);
        } catch (error) {
            console.log('Notification history endpoint not available, using localStorage');
            // Fallback to localStorage
            const saved = localStorage.getItem('notification_history');
            if (saved) {
                const history = JSON.parse(saved);
                setNotificationHistory(history);
                setFilteredHistory(history);
            }
        }
    } catch (error) {
        console.error('Error fetching notification history:', error);
        // Don't show error to user, just use empty array
        setNotificationHistory([]);
        setFilteredHistory([]);
    } finally {
        setLoadingHistory(false);
    }
};


  const sendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      setMessage('Please fill in both title and body');
      setMessageType('error');
      return;
    }

    if (targetType === 'specific' && !userId.trim()) {
      setMessage('Please enter a User ID');
      setMessageType('error');
      return;
    }

    setIsSending(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      
      let endpoint = '/api/admin/send-to-all';
      let payload = {
        title: title.trim(),
        body: body.trim(),
        user_type: userType === 'all' ? null : userType,
        data: {
          type: 'admin',
          sent_at: new Date().toISOString(),
        },
      };

      if (targetType === 'specific') {
        endpoint = '/api/admin/send-notification';
        payload = {
          ...payload,
          user_id: parseInt(userId),
          user_type: userType === 'all' ? 'rider' : userType,
        };
      }

      const response = await axios.post(`${API_URL}${endpoint}`, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Response:', response.data);

      if (response.data.success) {
        setMessage('✅ ' + (response.data.message || 'Notification sent successfully!'));
        setMessageType('success');
        
        // Save to history
        const historyEntry = {
          id: Date.now(),
          title: title.trim(),
          body: body.trim(),
          user_type: userType,
          target_type: targetType,
          recipient: targetType === 'specific' ? `User ${userId}` : 'All Users',
          sent_at: new Date().toISOString(),
          status: 'sent'
        };
        const updatedHistory = [historyEntry, ...notificationHistory];
        setNotificationHistory(updatedHistory);
        setFilteredHistory(updatedHistory);
        localStorage.setItem('notification_history', JSON.stringify(updatedHistory));
        
        setTitle('');
        setBody('');
        setUserId('');
      } else {
        setMessage('❌ ' + (response.data.message || 'Failed to send notification'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Send notification error:', error);
      setMessage('❌ Error: ' + (error.response?.data?.message || error.message));
      setMessageType('error');
    } finally {
      setIsSending(false);
    }
  };

  // Search stats
  const searchStats = {
    total: notificationHistory.length,
    shown: filteredHistory.length,
    hidden: notificationHistory.length - filteredHistory.length
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 'bold' }}>Send Notification</h1>
      <p style={{ color: '#64748b', marginBottom: '24px' }}>
        Send push notifications to drivers and riders
      </p>

      {/* Send Notification Form */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        maxWidth: '800px',
        marginBottom: '24px',
      }}>
        <h3 style={{ marginTop: 0 }}>📤 Send New Notification</h3>
        <hr style={{ margin: '16px 0' }} />

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Special Announcement"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
            Message
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Enter the message you want to send..."
            rows={4}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '16px',
              boxSizing: 'border-box',
              resize: 'vertical'
            }}
          />
        </div>

        <hr style={{ margin: '16px 0' }} />

        <h4 style={{ marginTop: 0 }}>Target Audience</h4>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
            Send to
          </label>
          <div style={{ display: 'flex', gap: '16px' }}>
            <label>
              <input
                type="radio"
                value="all"
                checked={targetType === 'all'}
                onChange={(e) => setTargetType(e.target.value)}
              /> All Users
            </label>
            <label>
              <input
                type="radio"
                value="specific"
                checked={targetType === 'specific'}
                onChange={(e) => setTargetType(e.target.value)}
              /> Specific User
            </label>
          </div>
        </div>

        {targetType === 'specific' && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              User ID
            </label>
            <input
              type="number"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Enter user ID (e.g., 123)"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
            User Type
          </label>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <label>
              <input
                type="radio"
                value="all"
                checked={userType === 'all'}
                onChange={(e) => setUserType(e.target.value)}
              /> All Types
            </label>
            <label>
              <input
                type="radio"
                value="driver"
                checked={userType === 'driver'}
                onChange={(e) => setUserType(e.target.value)}
              /> Drivers Only
            </label>
            <label>
              <input
                type="radio"
                value="rider"
                checked={userType === 'rider'}
                onChange={(e) => setUserType(e.target.value)}
              /> Riders Only
            </label>
          </div>
        </div>

        {message && (
          <div style={{
            padding: '12px',
            backgroundColor: messageType === 'success' ? '#dcfce7' : '#fee2e2',
            color: messageType === 'success' ? '#166534' : '#991b1b',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            {message}
          </div>
        )}

        <button
          onClick={sendNotification}
          disabled={isSending}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: '#f97316',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: isSending ? 'not-allowed' : 'pointer',
            opacity: isSending ? 0.7 : 1
          }}
        >
          {isSending ? 'Sending...' : '📤 Send Notification'}
        </button>
      </div>

      {/* Notification History */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        maxWidth: '800px',
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '16px',
        }}>
          <div>
            <h3 style={{ margin: 0 }}>📜 Notification History</h3>
            <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '13px', color: '#64748b' }}>
              <span>📬 {notificationHistory.length} total</span>
              {searchTerm && (
                <span style={{ color: '#f97316' }}>
                  🔍 {filteredHistory.length} shown
                  {searchStats.hidden > 0 && ` (${searchStats.hidden} hidden)`}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="🔍 Search history..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: '8px 14px',
                  paddingRight: searchTerm ? '36px' : '14px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  width: '220px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#94a3b8',
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            <button
              onClick={fetchNotificationHistory}
              style={{
                padding: '8px 14px',
                backgroundColor: '#f97316',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {loadingHistory ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            Loading history...
          </div>
        ) : filteredHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
            <p style={{ color: '#64748b' }}>
              {searchTerm ? `No notifications found matching "${searchTerm}"` : 'No notifications sent yet'}
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  padding: '6px 14px',
                  backgroundColor: '#f97316',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  marginTop: '8px',
                  fontSize: '13px',
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '13px' }}>Title</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '13px' }}>Recipient</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '13px' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '13px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '13px' }}>Sent At</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((notification) => (
                  <tr key={notification.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px', fontWeight: '500' }}>
                      {notification.title}
                    </td>
                    <td style={{ padding: '10px', fontSize: '13px', color: '#64748b' }}>
                      {notification.recipient || notification.user_type || 'All Users'}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        backgroundColor: notification.user_type === 'driver' ? '#dbeafe' : 
                                       notification.user_type === 'rider' ? '#dcfce7' : '#f1f5f9',
                        color: notification.user_type === 'driver' ? '#1e40af' : 
                               notification.user_type === 'rider' ? '#166534' : '#64748b',
                      }}>
                        {notification.user_type || 'All'}
                      </span>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        backgroundColor: notification.status === 'sent' ? '#dcfce7' : '#fef3c7',
                        color: notification.status === 'sent' ? '#166534' : '#92400e',
                      }}>
                        {notification.status || 'Sent'}
                      </span>
                    </td>
                    <td style={{ padding: '10px', fontSize: '12px', color: '#64748b' }}>
                      {formatDate(notification.sent_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredHistory.length > 0 && (
              <div style={{ 
                padding: '10px', 
                color: '#64748b', 
                fontSize: '12px',
                borderTop: '1px solid #e2e8f0',
                marginTop: '8px',
              }}>
                Showing {filteredHistory.length} of {notificationHistory.length} notifications
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{
        marginTop: '16px',
        padding: '16px',
        backgroundColor: '#fefce8',
        borderRadius: '8px',
        maxWidth: '800px',
        border: '1px solid #fef08a'
      }}>
        <h4 style={{ margin: 0, color: '#854d0e' }}>ℹ️ Info</h4>
        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#854d0e' }}>
          Users must be online to receive notifications. Admin notifications go through the backend API.
        </p>
      </div>
    </div>
  );
}

export default Notifications;